import { convertHeic } from "@keeratita/heic-converter";

const MAX_IMAGE_DIMENSION = 1024;
const IMAGE_QUALITY = 0.82;
const HEIC_FILE_EXTENSION = /\.hei[cf]$/i;
const HEIC_MEDIA_TYPE = /^image\/hei[cf](?:-sequence)?$/i;

type ImageProcessingStage =
	| "NATIVE_PROCESS"
	| "HEIC_CONVERT"
	| "CONVERTED_JPEG_PROCESS";

const IMAGE_PROCESSING_STAGE_LABELS: Record<ImageProcessingStage, string> = {
	NATIVE_PROCESS: "giải mã và thu nhỏ ảnh bằng trình duyệt",
	HEIC_CONVERT: "chuyển HEIC/HEIF sang JPEG",
	CONVERTED_JPEG_PROCESS: "đọc ảnh JPEG sau khi chuyển đổi",
};

class ImageProcessingError extends Error {
	constructor(stage: ImageProcessingStage, file: File, cause: unknown) {
		const detail =
			cause instanceof Error
				? `${cause.name}: ${cause.message}`
				: String(cause);
		const fileName = file.name || "không có tên";
		const mediaType = file.type || "không xác định";
		const sizeInMegabytes = (file.size / (1024 * 1024)).toFixed(2);
		super(
			`Không thể xử lý ảnh [${stage}]. Bước: ${IMAGE_PROCESSING_STAGE_LABELS[stage]}. Tệp: ${fileName}; loại: ${mediaType}; dung lượng: ${sizeInMegabytes} MB. Chi tiết: ${detail}`,
			{ cause },
		);
		this.name = "ImageProcessingError";
	}
}

export function isSupportedImage(file: File): boolean {
	return file.type.startsWith("image/") || HEIC_FILE_EXTENSION.test(file.name);
}

export function resizeImage(
	source: CanvasImageSource,
	width: number,
	height: number,
): string {
	const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(width, height));
	const canvas = document.createElement("canvas");
	canvas.width = Math.max(1, Math.round(width * scale));
	canvas.height = Math.max(1, Math.round(height * scale));

	const context = canvas.getContext("2d");
	if (!context) throw new Error("Trình duyệt không thể xử lý ảnh này.");

	context.fillStyle = "#fff";
	context.fillRect(0, 0, canvas.width, canvas.height);
	context.drawImage(source, 0, 0, canvas.width, canvas.height);
	return canvas.toDataURL("image/jpeg", IMAGE_QUALITY);
}

async function loadAndResizeImageFile(file: Blob): Promise<string> {
	const url = URL.createObjectURL(file);
	const image = new Image();

	try {
		await new Promise<void>((resolve, reject) => {
			image.onload = () => resolve();
			image.onerror = () =>
				reject(
					new DOMException(
						"Trình duyệt không thể tải dữ liệu ảnh.",
						"EncodingError",
					),
				);
			image.src = url;
		});
		return resizeImage(image, image.naturalWidth, image.naturalHeight);
	} finally {
		URL.revokeObjectURL(url);
	}
}

export async function resizeImageFile(file: File): Promise<string> {
	try {
		return await loadAndResizeImageFile(file);
	} catch (error) {
		if (
			!HEIC_MEDIA_TYPE.test(file.type) &&
			!HEIC_FILE_EXTENSION.test(file.name)
		) {
			throw new ImageProcessingError("NATIVE_PROCESS", file, error);
		}
	}

	let jpeg: Blob;
	try {
		jpeg = await convertHeic(file, {
			to: "jpeg",
			quality: IMAGE_QUALITY,
			maxWidth: MAX_IMAGE_DIMENSION,
			maxHeight: MAX_IMAGE_DIMENSION,
		});
	} catch (error) {
		throw new ImageProcessingError("HEIC_CONVERT", file, error);
	}

	try {
		return await loadAndResizeImageFile(jpeg);
	} catch (error) {
		throw new ImageProcessingError("CONVERTED_JPEG_PROCESS", file, error);
	}
}
